import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { API_BASE } from '../../../lib/api-base';
import { apiFetch } from '../../../lib/api-client';
import { useTranslation } from '../../../lib/i18n';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useMyAgents } from '../../../lib/my-agents';
import { useDuplexer } from '../../../lib/use-duplexer';
import { messageDir } from '../../../lib/message-direction';
import { LinkifiedText } from '../../LinkifiedText';
import { AgentDot } from '../../AgentDot';
import { ScrollArea, Typography } from '../ui';
import { buildRows, entityToMessage, isTerminal, type TranscriptResponse } from './agent-conversation-rows';
import styles from './TaskDetailsPanel.module.css';

// A read-only, LIVE, human transcript of the worker agent's own conversation — the "View Agents
// Conversation" surface. It reads like watching the agent work: the agent's own step narration and its
// final spoken turn are shown as prose, and each tool call is ONE muted human ACTION beat ("Sending a
// message to Avishai", "Reporting back to the Maestro") — never a tool name, params, raw id, or thinking.
// Each row is attributed to the agent that produced it (so worker↔reviewer reads as a dialogue) and
// stamped with its time. New rows STREAM in over the chat duplexer channel (filtered to this task's worker
// conversation), keeping the newest in view; a slow fallback poll keeps the task status honest and
// reconciles a dropped socket. Once the task finishes it settles. No composer. The fold + sanitizing live
// in `agent-conversation-rows.ts` (unit-tested, mirrored 1:1 by the native ports).

// The duplexer stream carries new rows live; this slow poll is only a safety net — it refreshes the task
// status (to notice the task finished) and reconciles anything a dropped socket missed.
const FALLBACK_POLL_MS = 15000;

export function AgentConversationPanel({ taskId, dashboardBuild = false }: { taskId: string; dashboardBuild?: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLElement>(null);
  const teamState = useMyAgents();
  const team = teamState.status === 'ready' ? teamState.team.agents : [];
  const agentName = (id?: string): string | undefined => (id ? team.find((a) => a.id === id)?.name : undefined);

  const { data, isError, isLoading } = useQuery({
    queryKey: ['task-agent-conversation', taskId],
    enabled: !!taskId,
    queryFn: async () => {
      const res = await apiFetch(`${API_BASE}/api/task/${encodeURIComponent(taskId)}/agent-conversation`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as TranscriptResponse;
    },
    // The duplexer streams new rows; this slow poll only refreshes status + reconciles a dropped socket.
    // It stops once the task is terminal (no more rows will come).
    refetchInterval: (data) => (isTerminal((data as TranscriptResponse | undefined)?.status) ? false : FALLBACK_POLL_MS),
  });
  useLoadBi('task-agent-conversation', { isLoading, isError });

  // Stream new rows live over the chat duplexer, filtered to THIS task's worker conversation, upserting
  // into the same cache the poll fills (dedupe by id, keep chronological). The tenant-checked endpoint
  // stays the initial load + authorization gate; the socket is only the "new row" signal, and it is
  // subscribed only while this panel is mounted (i.e. while someone is watching).
  const agentConversationId = data?.agentConversationId;
  useDuplexer((entity) => {
    if (!agentConversationId || entity.conversationId !== agentConversationId) return;
    const msg = entityToMessage(entity);
    if (!msg) return;
    queryClient.setQueryData<TranscriptResponse>(['task-agent-conversation', taskId], (prev) => {
      if (!prev) return prev;
      const existing = prev.messages ?? [];
      if (msg.id && existing.some((m) => m.id === msg.id)) return prev;
      const messages = [...existing, msg].sort((a, b) => (a.createdDate ?? '').localeCompare(b.createdDate ?? ''));
      return { ...prev, messages };
    });
  });

  const live = !isTerminal(data?.status);
  const rows = buildRows(data?.messages ?? []);

  // While the task is still running, keep the newest row in view as beats stream in — a live viewer
  // watches the work land at the bottom rather than having to scroll for it.
  useEffect(() => {
    if (!live) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [rows.length, live]);

  if (isLoading) {
    return (
      <div className={styles.skel}>
        <div className="orion-shimmer" style={{ height: 13, width: '80%' }} />
        <div className="orion-shimmer" style={{ height: 13, width: '60%' }} />
        <div className="orion-shimmer" style={{ height: 13, width: '72%' }} />
      </div>
    );
  }
  if (isError) {
    return <div className={styles.error}>{t('tasks.detail.conversationError')}</div>;
  }
  if (rows.length === 0) {
    // A dashboard build has no agent conversation to wait for — saying "as your agents start working"
    // here promises something that will never come true for this task kind.
    return <div className={styles.error}>{t(dashboardBuild ? 'tasks.detail.buildActivityEmpty' : 'agents.activity.empty')}</div>;
  }

  return (
    <ScrollArea ref={scrollRef} className={styles.transcript}>
      {rows.map((r, i) => {
        const name = agentName(r.agentId);
        // Show the agent header when the speaker changes (a new voice in the dialogue).
        const showAgent = i === 0 || rows[i - 1].agentId !== r.agentId;
        if (r.kind === 'act') {
          // A muted action step — the agent DOING something, between its spoken turns.
          return (
            <div key={i} className={styles.txAct}>
              <span className={styles.txActDot} aria-hidden />
              <span className={styles.txActText}>{r.text}</span>
              {r.time ? <span className={styles.txTime}>{r.time}</span> : null}
            </div>
          );
        }
        // A spoken turn: the agent's final message ('say', primary ink) or its own step narration
        // ('narrate', quieter — the agent thinking out loud as it works).
        return (
          <div key={i} className={styles.txTurn}>
            {(showAgent || r.time) && (
              <div className={styles.txAgent}>
                {showAgent && r.agentId ? <AgentDot id={r.agentId} size={16} /> : null}
                {showAgent && name ? <span className={styles.txAgentName}>{name}</span> : null}
                {r.time ? <span className={styles.txTime}>{r.time}</span> : null}
              </div>
            )}
            <Typography variant="p2" component="p" color={r.kind === 'narrate' ? 'secondary' : undefined} dir={messageDir(r.text)}>
              <LinkifiedText text={r.text} />
            </Typography>
          </div>
        );
      })}
      {live && (
        <div className={styles.txLive}>
          <span className={styles.txLiveDot} aria-hidden />
          <Typography variant="p3" component="span" color="secondary">
            {t('tasks.status.inProgress')}
          </Typography>
        </div>
      )}
    </ScrollArea>
  );
}
